/**
 * Connection-string orchestrator (Workstream B1; Option C C3 review r5 rules).
 *
 * 1. Forms that announce themselves are read first, in order: `jdbc:`; `postgres://` / `mysql://` URLs; SQLite paths and
 *    `sqlite:` / `file:` URIs; a string that starts with a Kusto address (Microsoft's URI-prefix form); an Azure Storage
 *    URL or bare SAS query; and, with no `=` at all, Oracle EZConnect.
 * 2. Any other `key=value` string is read in every keyword format whose own parser accepts it (`strictReadings`: libpq,
 *    ADO.NET, ODBC, Kusto, Azure Storage). None → refused. One → that one (for another engine than the pasting form's the
 *    dialog offers "Apply to <engine>"). Several that disagree about any secret's boundaries → refused, naming two
 *    (`refusal: 'conflict'`). Several that agree → one engine's; else the pasting form's engine (`options.engine`) picks
 *    among them; on the start view (or a form of none of their engines) rule 6. The form's engine never rescues a reading
 *    its format rejects. When the engine picked has two readings (SqlClient and ODBC) that fill a different target or TLS
 *    setting, the string is refused, naming the keywords it repeats (`refusal: 'repeated-keyword'`, `readingChoice.ts`,
 *    review round 5).
 * 3. No field but the secret receives `;` or `=` property text (C3-14): such a reading does not count, and when it was
 *    the only one the string is refused with that reason.
 * 4. A quoted or braced value that one format's own parser sees never close refuses the string (`refusal: 'unclosed'`):
 *    its author wrote that format's quoting, so no other format's reading of it is trusted.
 * 5. In any format, a value that opens with a quote or brace character the format does not treat as quoting means
 *    quoting it will not honour: that reading is not used, and when no other is, the string is refused naming the
 *    character (`refusal: 'unhonoured-quote'`).
 * 6. (Ruling after r5.) A password, user name or database that is entirely a portal placeholder (`{your_password}` …,
 *    `placeholders.ts`) is not given — never refused, never filled. Npgsql and MySqlConnector strings are ADO.NET
 *    readings of their own; several valid, agreeing readings for different engines that neither the pasting form's engine
 *    nor engine-only evidence (an Azure host suffix or SqlClient's own server syntax — a keyword or SSL Mode value only
 *    one accepts already leaves the others invalid) can tell apart are not refused and not guessed: `choices`, the
 *    engines to pick from.
 * 7. (Review round 5.) A SQL Server Encrypt / TrustServerCertificate value its driver rejects (or a blank one) refuses
 *    the string (`refusal: 'unreadable-value'`): nothing is patched, so a draft's Encrypt off never survives a new server.
 *
 * The raw input is NEVER logged, persisted, or sent to analytics.
 */

import type { DatabaseEngine } from '../../components/connectionFormSchema';
import type { ConnectionStringParseResult, ParserMatch } from './types';
import type { QuoteMark } from '@/core/utils/connectionStringTokens';
import { orderFields } from './helpers';
import { parseJdbc } from './parseJdbc';
import { parseUrlStyle } from './parseUrlStyle';
import { parseSqlitePath } from './parseSqlitePath';
import { parseEzConnect } from './parseEzConnect';
import { parseAzureStorage } from './parseAzureStorage';
import { namesKustoOnlyKeyword, parseKusto, startsWithKustoAddress } from './parseKusto';
import { ENGINE_OF_READING, holdsPropertyText, strictReadings, type Reading } from './connectionStringReadings';
import { withoutPlaceholderValues } from './placeholders';
import { disagreementFor, repeatedKeywordsIn, serverEvidence } from './readingChoice';

export type ParseOptions = {
  /** The engine of the form the text was pasted into: it settles a string whose format the rules cannot tell. */
  engine?: DatabaseEngine;
};

/** Why a result was refused although its format was recognised (fixed text, never a piece of the input). */
export const PROPERTY_TEXT_PROBLEM = 'a field other than the secret would hold connection-string text (";" or "=")';

const FAILED: ConnectionStringParseResult = {
  outcome: 'failed',
  format: 'unknown',
  values: {},
  matchedFields: [],
  unmatchedKeys: [],
};

/** A bare SAS query (`sv=…&sig=…`, optionally `?`-prefixed): no `;`, no whitespace. */
function isSasQuery(text: string): boolean {
  if (/[;\s]/u.test(text)) return false;
  const params = new URLSearchParams(text.replace(/^\?/u, ''));
  return params.has('sv') && params.has('sig');
}

/** The match of a form that announces itself; `undefined` when the text is a `key=value` string for the classifier. */
function announcedMatch(text: string): ParserMatch | null | undefined {
  if (/^jdbc:/iu.test(text)) return parseJdbc(text);
  for (const parser of [parseUrlStyle, parseSqlitePath]) {
    const match = parser(text);
    if (match) return match;
  }
  if (startsWithKustoAddress(text)) return parseKusto(text);
  if (/^https?:\/\//iu.test(text) || isSasQuery(text)) {
    const match = parseAzureStorage(text);
    if (match) return match;
  }
  if (!text.includes('=')) return parseEzConnect(text);
  return /^[a-z][a-z0-9+.-]*:\/\//iu.test(text) ? null : undefined;
}

/** The order the "Reads as" choice lists engines in. */
const CHOICE_ORDER: readonly DatabaseEngine[] = ['mssql', 'pgsql', 'mysql', 'oracle', 'sqlite', 'kusto', 'azurestorage'];

/**
 * Several agreeing readings, for different engines, the pasting form cannot settle: engine-only evidence (a keyword or SSL
 * Mode value only one accepts has already left the others invalid; here, what the server value names), else the choice.
 */
function settle(text: string, readings: readonly Reading[]): ParserMatch | ConnectionStringParseResult {
  const engineOf = (reading: Reading) => ENGINE_OF_READING[reading.format];
  const named = new Set(readings.map((reading) => serverEvidence(reading.match.values.server)).filter((engine) => engine !== undefined));
  const evident = named.size === 1 ? readings.find((reading) => named.has(engineOf(reading))) : undefined;
  if (evident) return readingFor(text, readings, engineOf(evident));
  const engines = new Set(readings.map(engineOf));
  const formats = new Set(readings.map((reading) => reading.match.format));
  return { ...FAILED, format: formats.size === 1 ? readings[0].match.format : 'unknown', choices: CHOICE_ORDER.filter((engine) => engines.has(engine)) };
}

/** `engine`'s reading — refused when two of its readings settle the string differently (`readingChoice.ts`, review r5). */
function readingFor(text: string, readings: readonly Reading[], engine: DatabaseEngine): ParserMatch | ConnectionStringParseResult {
  const disagreeing = disagreementFor(readings, engine);
  if (disagreeing) return { ...FAILED, refusal: 'repeated-keyword', conflict: disagreeing, repeatedKeywords: repeatedKeywordsIn(text) };
  return readings.find((reading) => ENGINE_OF_READING[reading.format] === engine)?.match ?? { ...FAILED };
}

function refusedForPropertyText(match: ParserMatch): ParserMatch {
  return { format: match.format, engine: match.engine, values: {}, matchedFields: [], unmatchedKeys: [], problem: PROPERTY_TEXT_PROBLEM };
}

/** The keyword-string rules (step 2): the one reading to use, or the refusal. */
function keywordMatch(text: string, engine: DatabaseEngine | undefined): ParserMatch | ConnectionStringParseResult | null {
  const { readings, propertyText, quotingBroken, unhonouredQuote } = strictReadings(text);
  // A quoted value one format's parser sees never close: whatever else reads the text, the author meant that quoting.
  if (quotingBroken.length > 0) return { ...FAILED, refusal: 'unclosed' };
  if (readings.length === 0) {
    // The only formats that read it would take a quote they do not honour as part of a value: refused, naming it.
    const unhonoured = unhonouredQuote.find((reading) => engine && ENGINE_OF_READING[reading.format] === engine) ?? unhonouredQuote[0];
    if (unhonoured) return { ...FAILED, refusal: 'unhonoured-quote', unhonouredQuote: unhonoured };
    if (propertyText[0]) return refusedForPropertyText(propertyText[0].match);
    // No valid reading: a string that names Kusto-only keywords is refused with the Kusto reason (quotes, a keyword the
    // SDK does not know …) — a problem that fills nothing, never a value.
    const kusto = namesKustoOnlyKeyword(text) ? parseKusto(text) : null;
    return kusto?.problem ? kusto : null;
  }
  if (readings.length === 1) return readings[0].match;
  const secrets = (index: number) => JSON.stringify(readings[index].secrets);
  const differing = readings.findIndex((_reading, index) => secrets(index) !== secrets(0));
  if (differing !== -1) return { ...FAILED, refusal: 'conflict', conflict: [readings[0].format, readings[differing].format] };
  const engines = new Set(readings.map((reading) => ENGINE_OF_READING[reading.format]));
  if (engines.size === 1) return readingFor(text, readings, ENGINE_OF_READING[readings[0].format]);
  return engine && engines.has(engine) ? readingFor(text, readings, engine) : settle(text, readings);
}

const isResult = (value: ParserMatch | ConnectionStringParseResult): value is ConnectionStringParseResult => 'outcome' in value;

/**
 * URLs, JDBC URLs and EZConnect have no quoting of their own (a JDBC SQL Server property's braces are checked by its
 * scanner): a value that opens with a quote or brace means quoting they will not honour (the r5 ruling) — refused.
 */
function announcedUnhonouredQuote(match: ParserMatch): ConnectionStringParseResult['unhonouredQuote'] | undefined {
  if (match.format !== 'url' && match.format !== 'jdbc' && match.format !== 'ezconnect') return undefined;
  if (match.opening) return { format: match.format, quote: match.opening };
  if (match.format === 'jdbc' && match.engine === 'mssql') return undefined;
  for (const value of Object.values(match.values)) {
    if (typeof value === 'string' && /^["'{]/u.test(value)) return { format: match.format, quote: value[0] as QuoteMark };
  }
  return undefined;
}

export function parseConnectionString(input: string, options: ParseOptions = {}): ConnectionStringParseResult {
  const trimmed = (input ?? '').trim();
  if (!trimmed) return { ...FAILED };

  const announced = announcedMatch(trimmed);
  const found = announced === undefined ? keywordMatch(trimmed, options.engine) : announced;
  if (!found) return { ...FAILED };
  if (isResult(found)) return found;
  if (found.tlsUnreadable) return { ...FAILED, refusal: 'unreadable-value', unreadable: found.tlsUnreadable };
  // A URL's, JDBC URL's, EZConnect's or Kusto's value as read is the value as written (keyword readings dropped theirs).
  const given = withoutPlaceholderValues(found);
  const unhonoured = announcedUnhonouredQuote(given);
  if (unhonoured) return { ...FAILED, refusal: 'unhonoured-quote', unhonouredQuote: unhonoured };
  const match = holdsPropertyText(given) ? refusedForPropertyText(given) : given;

  // A recognised string that cannot be used as it is (a problem) is partial: nothing it holds is filled.
  const outcome = match.unmatchedKeys.length > 0 || match.problem ? 'partial' : 'full';
  const result: ConnectionStringParseResult = {
    outcome,
    format: match.format,
    engine: match.engine,
    values: match.values,
    matchedFields: orderFields(match.matchedFields),
    unmatchedKeys: match.unmatchedKeys,
  };
  if (match.suggestEntra) result.suggestEntra = true;
  if (match.encryptStrict) result.encryptStrict = true;
  if (match.problem) result.problem = match.problem;
  return result;
}

export type { ConnectionStringParseResult, ConnectionStringFormat, ReadingFormat } from './types';
